import { useQuery } from '@apollo/client';
import { Checkbox, CircularProgress, FormControlLabel } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { PageHeading } from 'lead-list/components/PageHeading';
import { Paper } from 'lead-list/components/Paper';
import { Select } from 'lead-list/components/Select';
import { GlobalState, useStateMachine } from 'little-state-machine';
import { LeadListTypeIcon } from 'll-components/LeadListTypeIcon/LeadListTypeIcon';
import { useAuthContext } from 'providers/auth';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Navigate, useNavigate, useParams } from 'react-router';
import {
  GetCompanyCampaigns,
  GetCompanyCampaigns_getCampaigns_items,
} from 'types/apollo/GetCompanyCampaigns';
import { ListType } from 'types/apollo/globalTypes';
import { STEPS } from '../../LeadListCreate';
import { predictLeadListMappings } from '../ListColumnMapper/leadListMappings';
import { Nav } from '../Nav';
import { StepContainer } from '../StepContainer';
import { GET_COMPANY_CAMPAIGNS } from './getCompanyCampaigns.graphql';
import messages from './messages';
import { styles } from './styles';

interface IUpdateInput {
  listType: ListType;
  campaign: GetCompanyCampaigns_getCampaigns_items | null;
  mappings: GlobalState['leadListCreate']['mappings'];
}

const updateListDetails = (state: GlobalState, { listType, campaign, mappings }: IUpdateInput) => ({
  ...state,
  leadListCreate: {
    ...state.leadListCreate,
    listType,
    campaign,
    mappings,
  },
});

const setHasSeenFileUpload = (state: GlobalState, hasSeen: boolean) => ({
  ...state,
  leadListCreate: {
    ...state.leadListCreate,
    hasSeenFileUpload: hasSeen,
  },
});

const setExternalCompanyId = (state: GlobalState, externalCompanyId?: number) => ({
  ...state,
  leadListCreate: {
    ...state.leadListCreate,
    externalCompanyId,
  },
});

interface IFormInput {
  listType: ListType;
  shortId: string;
  useCampaign: boolean;
}

const actionsMap = { updateListDetails, setHasSeenFileUpload, setExternalCompanyId } as const;

export const StepSelectLeadType: FC = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { id: companyIdParam } = useParams<{ id: string }>();
  const { isInternal } = useAuthContext();

  const companyId = useMemo(
    () => (isInternal && companyIdParam ? parseInt(companyIdParam, 10) : undefined),
    [isInternal, companyIdParam],
  );

  const {
    actions,
    state: { leadListCreate },
  } = useStateMachine(actionsMap);

  const { control, handleSubmit, watch, setValue, getValues } = useForm<IFormInput>({
    defaultValues: {
      listType: leadListCreate.listType || ListType.PROSPECTIVE,
      shortId: `${leadListCreate?.campaign?.shortId || ''}`,
      useCampaign: false,
    },
  });

  const [goGreen] = useState(false);

  const keyOf = (c: GetCompanyCampaigns_getCampaigns_items) => c.shortId ?? '';

  const listType = watch('listType');
  const shortId = watch('shortId');
  const useCampaign = watch('useCampaign');

  const skipCampaigns =
    listType === ListType.DNC || listType === ListType.CLOSE || (isInternal && !companyId);

  const { data, loading } = useQuery<GetCompanyCampaigns>(GET_COMPANY_CAMPAIGNS, {
    variables: {
      ...(isInternal && companyId ? { companyId } : {}),
      pagination: { limit: 100 },
      isActive: { equals: 'LIVE' as const },
    },
    fetchPolicy: 'network-only',
    skip: skipCampaigns,
  });

  const campaigns = useMemo(() => data?.getCampaigns?.items ?? [], [data]);

  const useCampaignDropDown =
    listType === ListType.PROSPECTIVE || (listType === ListType.ENDCONTACT && useCampaign);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => keyOf(c) === shortId),
    [campaigns, shortId],
  );
  const isCampaignValid = useCampaignDropDown ? Boolean(selectedCampaign) : true;

  const nextStep = () => navigate(`../${STEPS.mapColumns}`);

  const onSubmit = handleSubmit((form) => {
    const chosen = campaigns.find((c) => keyOf(c) === form.shortId);

    if (isInternal && !companyId) {
      throw new Error('Unable to load company information. Please try again.');
    }

    const mappings = predictLeadListMappings(
      leadListCreate.fileHeaders ?? [],
      form.listType,
      chosen?.shortId ?? null,
    );

    actions.updateListDetails({
      listType: form.listType,
      campaign: form.listType === ListType.DNC ? null : chosen ?? null,
      mappings,
    });

    nextStep();
  });

  const submitForm = useCallback(() => onSubmit(), [onSubmit]);

  // Auto-advance whenever the user changes TO DNC or CLOSE (but not on initial mount)
  const isFirstRenderRef = useRef(true);
  const previousListTypeRef = useRef<ListType | null>(null);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      previousListTypeRef.current = listType;
      return;
    }

    const prev = previousListTypeRef.current;
    previousListTypeRef.current = listType;

    const nowIsAutoType = listType === ListType.DNC || listType === ListType.CLOSE;
    const prevIsAutoType = prev === ListType.DNC || prev === ListType.CLOSE;

    const transitionedIntoAutoType = nowIsAutoType && !prevIsAutoType;

    if (transitionedIntoAutoType) {
      submitForm();
    }
  }, [listType, submitForm]);

  useEffect(() => {
    if (leadListCreate.listType) {
      setValue('listType', leadListCreate.listType, { shouldDirty: false, shouldValidate: false });
    }
  }, [leadListCreate.listType, setValue]);

  useEffect(() => {
    const saved = leadListCreate.campaign;
    if (!getValues('shortId') && saved) {
      setValue('shortId', saved.shortId ?? '', { shouldDirty: false, shouldValidate: true });
    }
  }, [leadListCreate.campaign, getValues, setValue]);

  useEffect(() => {
    if (!useCampaign) setValue('shortId', '');
  }, [useCampaign, setValue]);

  useEffect(() => {
    if (!useCampaignDropDown) return;
    if (!shortId) return;
    if (!campaigns.some((c) => keyOf(c) === shortId)) {
      setValue('shortId', '', { shouldDirty: false, shouldValidate: true });
    }
  }, [useCampaignDropDown, campaigns, shortId, setValue]);

  useEffect(() => {
    if (!isInternal || !companyId) return;
    if (leadListCreate.externalCompanyId === companyId) return;
    actions.setExternalCompanyId(companyId);
  }, [isInternal, companyId, leadListCreate.externalCompanyId]);

  if (!leadListCreate.file?.name) {
    return <Navigate to=".." replace relative="path" />;
  }

  return (
    <form onSubmit={onSubmit}>
      <StepContainer>
        <PageHeading title={formatMessage(messages.assignTitle)} />

        <Typography variant="body1" sx={styles.fileName}>
          {leadListCreate.file.name}
        </Typography>

        <Paper
          variant="outlined"
          shade={goGreen ? 'success' : undefined}
          sx={styles.paperContainer}
        >
          <Box sx={styles.selectContainer}>
            <Controller
              name="listType"
              control={control}
              render={({ field }) => (
                <Select {...field} label={formatMessage(messages.listType)} sx={styles.select}>
                  <MenuItem value={ListType.PROSPECTIVE}>
                    <Box sx={{ pointerEvents: 'none' }}>
                      <LeadListTypeIcon listType={ListType.PROSPECTIVE} withLabel />
                    </Box>
                  </MenuItem>

                  <MenuItem value={ListType.CLOSE}>
                    <Box sx={{ pointerEvents: 'none' }}>
                      <LeadListTypeIcon listType={ListType.CLOSE} withLabel />
                    </Box>
                  </MenuItem>

                  <MenuItem value={ListType.ENDCONTACT}>
                    <Box sx={{ pointerEvents: 'none' }}>
                      <LeadListTypeIcon listType={ListType.ENDCONTACT} withLabel />
                    </Box>
                  </MenuItem>

                  <MenuItem value={ListType.DNC}>
                    <Box sx={{ pointerEvents: 'none' }}>
                      <LeadListTypeIcon listType={ListType.DNC} withLabel />
                    </Box>
                  </MenuItem>
                </Select>
              )}
            />

            {listType === ListType.ENDCONTACT && (
              <Controller
                name="useCampaign"
                control={control}
                render={() => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useCampaign}
                        onChange={() => setValue('useCampaign', !useCampaign)}
                      />
                    }
                    label="End contact for campaign"
                  />
                )}
              />
            )}

            {useCampaignDropDown && (
              <>
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, ml: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatMessage(messages.loadingCampaigns)}
                    </Typography>
                  </Box>
                ) : campaigns.length > 0 ? (
                  <Controller
                    name="shortId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label={formatMessage(messages.campaign)}
                        sx={styles.select}
                        disabled={isInternal && !companyId}
                      >
                        {campaigns.map((campaign) => (
                          <MenuItem key={keyOf(campaign)} value={keyOf(campaign)}>
                            {campaign.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                ) : (
                  <Typography sx={{ mt: 2, ml: 1 }} color="text.secondary">
                    {formatMessage(
                      messages.noCampaignsFound ?? { defaultMessage: 'No campaigns found' },
                    )}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Paper>

        <Nav
          onBackClick={() => navigate(-1)}
          onForwardClick={isCampaignValid ? onSubmit : undefined}
          disableForward={!isCampaignValid || goGreen || (isInternal && !companyId)}
          disableBack={goGreen}
        />
      </StepContainer>
    </form>
  );
};
